import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchWordFamily, WordFamilyError, type WordFamilyResponse } from "@/lib/philology/api";
import { normalizeWord } from "@/lib/philology/normalizeWord";

export interface UseWordFamilyOptions {
  word: string;
  language: string;
  cardId?: string | null;
  sourcePhrase?: string | null;
  enabled?: boolean;
}

const STALE_FOREVER = 1000 * 60 * 60 * 24 * 365;

export function useWordFamily({ word, language, cardId, sourcePhrase, enabled = true }: UseWordFamilyOptions) {
  const normalized = normalizeWord(word ?? "", language ?? "");
  const queryClient = useQueryClient();
  const queryKey = ["word-family", normalized, language] as const;

  const query = useQuery<WordFamilyResponse, WordFamilyError>({
    queryKey,
    queryFn: ({ signal }) =>
      fetchWordFamily({
        word,
        language,
        cardId: cardId ?? null,
        sourcePhrase: sourcePhrase ?? null,
        signal,
      }),
    enabled: enabled && Boolean(normalized) && Boolean(language),
    staleTime: STALE_FOREVER,
    gcTime: STALE_FOREVER,
    retry: (failureCount, error) => {
      if (!(error instanceof WordFamilyError)) return failureCount < 1;
      if (error.status >= 400 && error.status < 500) return false;
      return failureCount < 1;
    },
  });

  function regenerate() {
    return queryClient.fetchQuery({
      queryKey,
      queryFn: ({ signal }) =>
        fetchWordFamily({
          word,
          language,
          cardId: cardId ?? null,
          sourcePhrase: sourcePhrase ?? null,
          force: true,
          signal,
        }),
      staleTime: 0,
    });
  }

  return { ...query, regenerate };
}
